import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { rideService } from '@/services/rideService';
import { mapsService } from '@/services/mapsService';
import { chatService } from '@/services/chatService';
import { notificationService } from '@/services/notificationService';
import { useAuth } from './AuthContext';
import type { Ride, Location, GeoPoint, Stop, Message } from '@/types';

interface RideContextType {
  // Current ride state
  currentRide: Ride | null;
  isSearching: boolean;
  
  // Location state
  pickup: Location | null;
  destination: Location | null;
  stops: Stop[];
  currentLocation: GeoPoint | null;
  
  // Ride info
  estimatedPrice: number;
  estimatedDistance: number;
  estimatedDuration: number;
  
  // Chat
  messages: Message[];
  unreadCount: number;
  
  // Actions
  setPickup: (location: Location | null) => void;
  setDestination: (location: Location | null) => void;
  addStop: (location: Location) => void;
  removeStop: (stopId: string) => void;
  reorderStops: (newOrder: string[]) => void;
  
  // Ride actions
  requestRide: (rideType: string, paymentMethod: string) => Promise<string>;
  acceptRide: (rideId: string) => Promise<void>;
  cancelRide: (reason: string) => Promise<void>;
  startRide: () => Promise<void>;
  completeRide: () => Promise<void>;
  driverArrived: () => Promise<void>;
  
  // Chat actions
  sendMessage: (content: string) => Promise<void>;
  sendQuickMessage: (index: number) => Promise<void>;
  markMessagesAsRead: () => Promise<void>;
  
  // Location
  updateCurrentLocation: () => Promise<void>;
  startTracking: () => void;
  stopTracking: () => void;
  
  // Subscribe to ride
  subscribeToRide: (rideId: string) => void;
  unsubscribeFromRide: () => void;
}

const RideContext = createContext<RideContextType | undefined>(undefined);

export const RideProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userType } = useAuth();
  
  // Ride state
  const [currentRide, setCurrentRide] = useState<Ride | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Location state
  const [pickup, setPickupState] = useState<Location | null>(null);
  const [destination, setDestinationState] = useState<Location | null>(null);
  const [stops, setStops] = useState<Stop[]>([]);
  const [currentLocation, setCurrentLocation] = useState<GeoPoint | null>(null);
  
  // Ride info
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [estimatedDistance, setEstimatedDistance] = useState(0);
  const [estimatedDuration, setEstimatedDuration] = useState(0);
  
  // Chat
  const [messages, setMessages] = useState<Message[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Subscriptions
  const [rideUnsubscribe, setRideUnsubscribe] = useState<(() => void) | null>(null);
  const [messagesUnsubscribe, setMessagesUnsubscribe] = useState<(() => void) | null>(null);

  // Set pickup and update route
  const setPickup = useCallback((location: Location | null) => {
    setPickupState(location);
    updateRouteInfo(location, destination, stops);
  }, [destination, stops]);

  // Set destination and update route
  const setDestination = useCallback((location: Location | null) => {
    setDestinationState(location);
    updateRouteInfo(pickup, location, stops);
  }, [pickup, stops]);

  // Add stop
  const addStop = useCallback((location: Location) => {
    const newStop: Stop = {
      id: `stop_${Date.now()}`,
      order: stops.length + 1,
      location,
    };
    setStops([...stops, newStop]);
    updateRouteInfo(pickup, destination, [...stops, newStop]);
  }, [pickup, destination, stops]);

  // Remove stop
  const removeStop = useCallback((stopId: string) => {
    const newStops = stops.filter(s => s.id !== stopId);
    // Reorder
    newStops.forEach((stop, index) => {
      stop.order = index + 1;
    });
    setStops(newStops);
    updateRouteInfo(pickup, destination, newStops);
  }, [pickup, destination, stops]);

  // Reorder stops
  const reorderStops = useCallback((newOrder: string[]) => {
    const newStops = newOrder
      .map(id => stops.find(s => s.id === id))
      .filter((s): s is Stop => !!s);
    
    newStops.forEach((stop, index) => {
      stop.order = index + 1;
    });
    
    setStops(newStops);
    updateRouteInfo(pickup, destination, newStops);
  }, [pickup, destination, stops]);

  // Update route info
  const updateRouteInfo = async (
    pickupLoc: Location | null,
    destLoc: Location | null,
    stopsList: Stop[]
  ) => {
    if (!pickupLoc || !destLoc) {
      setEstimatedPrice(0);
      setEstimatedDistance(0);
      setEstimatedDuration(0);
      return;
    }

    const waypoints = stopsList.map(s => s.location.coordinates);
    
    const route = await mapsService.calculateRoute(
      pickupLoc.coordinates,
      destLoc.coordinates,
      waypoints
    );

    if (route) {
      setEstimatedDistance(route.distance);
      setEstimatedDuration(route.duration);
      setEstimatedPrice(
        rideService.calculatePrice(route.distance, route.duration)
      );
    }
  };

  // Request ride
  const requestRide = useCallback(async (
    rideType: string,
    paymentMethod: string
  ): Promise<string> => {
    if (!currentUser || !pickup || !destination) {
      throw new Error('Missing required information');
    }

    setIsSearching(true);
    
    try {
      const rideId = await rideService.createRide({
        passengerId: currentUser.id,
        type: rideType as any,
        pickup,
        destination,
        stops,
        estimatedDistance,
        estimatedDuration,
        estimatedPrice,
        paymentMethod: paymentMethod as any,
      });

      // Subscribe to ride updates
      subscribeToRide(rideId);

      // Notify nearby drivers
      // In production, use Cloud Function

      return rideId;
    } finally {
      setIsSearching(false);
    }
  }, [currentUser, pickup, destination, stops, estimatedDistance, estimatedDuration, estimatedPrice]);

  // Accept ride (driver)
  const acceptRide = useCallback(async (rideId: string) => {
    if (!currentUser || userType !== 'driver') return;

    await rideService.assignDriver(rideId, currentUser.id);
    subscribeToRide(rideId);
  }, [currentUser, userType]);

  // Cancel ride
  const cancelRide = useCallback(async (reason: string) => {
    if (!currentRide) return;

    await rideService.cancelRide(
      currentRide.id,
      reason,
      userType === 'passenger' ? 'passenger' : 'driver'
    );
    
    unsubscribeFromRide();
    setCurrentRide(null);
  }, [currentRide, userType]);

  // Start ride
  const startRide = useCallback(async () => {
    if (!currentRide) return;
    await rideService.startRide(currentRide.id);
  }, [currentRide]);

  // Complete ride
  const completeRide = useCallback(async () => {
    if (!currentRide) return;
    
    const finalPrice = currentRide.finalPrice || currentRide.estimatedPrice;
    await rideService.completeRide(currentRide.id, finalPrice);
    
    unsubscribeFromRide();
    setCurrentRide(null);
    setPickupState(null);
    setDestinationState(null);
    setStops([]);
  }, [currentRide]);

  // Driver arrived
  const driverArrived = useCallback(async () => {
    if (!currentRide) return;
    await rideService.driverArrived(currentRide.id);
  }, [currentRide]);

  // Send message
  const sendMessage = useCallback(async (content: string) => {
    if (!currentRide || !currentUser) return;

    await chatService.sendTextMessage(
      currentRide.id,
      currentUser.id,
      userType as 'passenger' | 'driver',
      content
    );
  }, [currentRide, currentUser, userType]);

  // Send quick message
  const sendQuickMessage = useCallback(async (index: number) => {
    if (!currentRide || !currentUser || !userType) return;

    await chatService.sendQuickMessage(
      currentRide.id,
      currentUser.id,
      userType as 'passenger' | 'driver',
      index
    );
  }, [currentRide, currentUser, userType]);

  // Mark messages as read
  const markMessagesAsRead = useCallback(async () => {
    if (!currentRide || !currentUser) return;
    
    await chatService.markAllAsRead(currentRide.id, currentUser.id);
    setUnreadCount(0);
  }, [currentRide, currentUser]);

  // Update current location
  const updateCurrentLocation = useCallback(async () => {
    try {
      const position = await mapsService.getCurrentPosition();
      setCurrentLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    } catch (error) {
      console.error('Error getting location:', error);
    }
  }, []);

  // Start location tracking
  const startTracking = useCallback(() => {
    mapsService.startLocationTracking(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCurrentLocation(newLocation);
        
        // Update driver location in database
        if (currentUser && userType === 'driver') {
          // driverService.updateLocation(currentUser.id, newLocation.lat, newLocation.lng);
        }
      },
      (error) => {
        console.error('Location tracking error:', error);
      }
    );
  }, [currentUser, userType]);

  // Stop location tracking
  const stopTracking = useCallback(() => {
    mapsService.stopLocationTracking();
  }, []);

  // Subscribe to ride updates
  const subscribeToRide = useCallback((rideId: string) => {
    // Unsubscribe from previous ride
    unsubscribeFromRide();

    // Subscribe to ride
    const unsubscribeRide = rideService.subscribeToRide(rideId, (ride) => {
      setCurrentRide(ride);
    });
    setRideUnsubscribe(() => unsubscribeRide);

    // Subscribe to messages
    const unsubscribeMessages = chatService.subscribeToRideMessages(rideId, (msgs) => {
      setMessages(msgs);
      
      // Count unread messages
      if (currentUser) {
        const unread = msgs.filter(
          m => m.senderId !== currentUser.id && !m.readAt
        ).length;
        setUnreadCount(unread);
      }
    });
    setMessagesUnsubscribe(() => unsubscribeMessages);
  }, [currentUser]);

  // Unsubscribe from ride
  const unsubscribeFromRide = useCallback(() => {
    if (rideUnsubscribe) {
      rideUnsubscribe();
      setRideUnsubscribe(null);
    }
    if (messagesUnsubscribe) {
      messagesUnsubscribe();
      setMessagesUnsubscribe(null);
    }
    setMessages([]);
    setUnreadCount(0);
  }, [rideUnsubscribe, messagesUnsubscribe]);

  // Check for active ride on mount
  useEffect(() => {
    const checkActiveRide = async () => {
      if (!currentUser || !userType) return;

      let activeRide: Ride | null = null;

      if (userType === 'passenger') {
        activeRide = await rideService.getPassengerActiveRide(currentUser.id);
      } else if (userType === 'driver') {
        activeRide = await rideService.getDriverActiveRide(currentUser.id);
      }

      if (activeRide) {
        setCurrentRide(activeRide);
        subscribeToRide(activeRide.id);
      }
    };

    checkActiveRide();
  }, [currentUser, userType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribeFromRide();
      stopTracking();
    };
  }, []);

  const value: RideContextType = {
    currentRide,
    isSearching,
    pickup,
    destination,
    stops,
    currentLocation,
    estimatedPrice,
    estimatedDistance,
    estimatedDuration,
    messages,
    unreadCount,
    setPickup,
    setDestination,
    addStop,
    removeStop,
    reorderStops,
    requestRide,
    acceptRide,
    cancelRide,
    startRide,
    completeRide,
    driverArrived,
    sendMessage,
    sendQuickMessage,
    markMessagesAsRead,
    updateCurrentLocation,
    startTracking,
    stopTracking,
    subscribeToRide,
    unsubscribeFromRide,
  };

  return <RideContext.Provider value={value}>{children}</RideContext.Provider>;
};

export const useRide = () => {
  const context = useContext(RideContext);
  if (context === undefined) {
    throw new Error('useRide must be used within a RideProvider');
  }
  return context;
};
